import React, { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Full-screen image lightbox with keyboard, arrow-button and click-outside
 * dismissal. Accepts an ordered list of image URLs and the index currently
 * being displayed.
 */
export const Lightbox = ({ images, index, onClose, onIndexChange, alt = "" }) => {
  const total = images?.length || 0;
  const canPrev = total > 1;
  const canNext = total > 1;

  const go = useCallback(
    (delta) => {
      if (!total) return;
      const next = (index + delta + total) % total;
      onIndexChange(next);
    },
    [index, total, onIndexChange]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && canPrev) go(-1);
      else if (e.key === "ArrowRight" && canNext) go(1);
    };
    window.addEventListener("keydown", onKey);
    // Lock body scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, go, canPrev, canNext]);

  if (total === 0) return null;
  const src = images[index];

  return (
    <div
      data-testid="lightbox"
      className="fixed inset-0 z-[200] bg-black/95 backdrop-blur-md flex items-center justify-center"
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-6 py-5 text-white/80">
        <span className="text-xs uppercase tracking-widest font-bold font-mono-num">
          {index + 1} / {total}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          data-testid="lightbox-close"
          aria-label="Close"
          className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 transition-colors flex items-center justify-center"
        >
          <X size={20} />
        </button>
      </div>

      {/* Prev */}
      {canPrev && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            go(-1);
          }}
          data-testid="lightbox-prev"
          aria-label="Previous image"
          className="absolute left-4 md:left-8 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors flex items-center justify-center"
        >
          <ChevronLeft size={26} />
        </button>
      )}

      {/* Main image */}
      <img
        key={src}
        src={src}
        alt={alt}
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
        className="max-w-[92vw] max-h-[82vh] object-contain rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200"
      />

      {/* Next */}
      {canNext && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            go(1);
          }}
          data-testid="lightbox-next"
          aria-label="Next image"
          className="absolute right-4 md:right-8 w-12 h-12 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors flex items-center justify-center"
        >
          <ChevronRight size={26} />
        </button>
      )}

      {/* Thumbnail strip */}
      {total > 1 && (
        <div className="absolute bottom-6 inset-x-0 flex justify-center gap-2 px-4 overflow-x-auto pb-1">
          {images.map((u, i) => (
            <button
              key={`${u}-${i}`}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onIndexChange(i);
              }}
              data-testid={`lightbox-thumb-${i}`}
              className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition-all ${i === index ? "border-[#3DDC97]" : "border-transparent opacity-60 hover:opacity-100"}`}
            >
              <img src={u} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
